import { AlertCircle, CalendarClock, CheckCircle2, CircleSlash, Clock, Languages, Loader2, Scissors, Send } from "lucide-react";
import type { JobStatus } from "../api";

/** Library-screen overlay statuses. Not real JobStatus values — they
 *  come from cross-referencing scheduled posts with library items. */
export type DisplayStatus = JobStatus | "scheduled" | "published";

interface Props {
  status: DisplayStatus;
  progress?: number;
}

const map: Record<DisplayStatus, { label: string; color: string; bg: string; border: string; Icon: typeof Clock; spin?: boolean }> = {
  ready:        { label: "Ready",        color: "#4ADE80", bg: "rgba(34,197,94,.15)",   border: "rgba(74,222,128,.3)",  Icon: CheckCircle2 },
  rendering:    { label: "Rendering",    color: "#FFD500", bg: "rgba(255,213,0,.15)",   border: "rgba(255,213,0,.3)",   Icon: Loader2, spin: true },
  transcribing: { label: "Transcribing", color: "#60A5FA", bg: "rgba(96,165,250,.15)",  border: "rgba(96,165,250,.3)",  Icon: Loader2, spin: true },
  translating:  { label: "Translating",  color: "#A78BFA", bg: "rgba(167,139,250,.15)", border: "rgba(167,139,250,.3)", Icon: Languages },
  clipping:     { label: "Clipping",     color: "#60A5FA", bg: "rgba(96,165,250,.15)",  border: "rgba(96,165,250,.3)",  Icon: Scissors },
  queued:       { label: "Queued",       color: "#9CA3AF", bg: "rgba(156,163,175,.15)", border: "rgba(156,163,175,.3)", Icon: Clock },
  failed:       { label: "Failed",       color: "#F87171", bg: "rgba(248,113,113,.15)", border: "rgba(248,113,113,.3)", Icon: AlertCircle },
  incomplete:   { label: "Incomplete",   color: "#FACC15", bg: "rgba(250,204,21,.10)",  border: "rgba(250,204,21,.30)", Icon: CircleSlash },
  scheduled:    { label: "Scheduled",    color: "#7BA0FF", bg: "rgba(123,160,255,.15)", border: "rgba(123,160,255,.32)", Icon: CalendarClock },
  published:    { label: "Published",    color: "#7BD389", bg: "rgba(123,211,137,.14)", border: "rgba(123,211,137,.32)", Icon: Send },
};

export function StatusPill({ status, progress }: Props): React.JSX.Element {
  const s = map[status] ?? map.queued;
  const showProgress =
    progress !== undefined &&
    progress > 0 &&
    progress < 1 &&
    status !== "ready" &&
    status !== "failed" &&
    status !== "scheduled" &&
    status !== "published";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.border}`,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <s.Icon size={12} strokeWidth={2} className={s.spin ? "ql-spin" : undefined} />
      {s.label}
      {showProgress && <span style={{ opacity: 0.8 }}>· {Math.round(progress * 100)}%</span>}
    </span>
  );
}
